import type { TagToggleProps } from './tagToggle.types'

import { styled, useStyleConfig, vui } from '../core'
import Icon from '../icon'
import T from '../t'
import { fontSizes } from '../theme'
import { cs, isString } from '../utils'

// ---------------------------------------------------------------------------
// Styled primitive
// ---------------------------------------------------------------------------

const TagToggleBase = styled.buttonBox`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  font-weight: medium;
  justify-content: center;
  line-height: 1;
  position: relative;
  transition-duration: fast;
  user-select: none;
  width: fit-content;

  &:focus {
    outline: none;
  }
`

// ---------------------------------------------------------------------------
// TagToggle component
// ---------------------------------------------------------------------------

/**
 * A toggle button styled as a tag. Uses brand-only colour tokens and an
 * `aria-pressed` attribute to convey selected state.
 */
export const TagToggle = vui<'button', TagToggleProps>((props, ref) => {
  const { className, icon, isSelected = false, label, onChange, variant = 'solid', ...rest } = props

  // Resolve the theme variant key: e.g. subtle → subtle, subtle + selected → subtleSelected.
  const themeVariant = isSelected ? `${variant}Selected` : variant

  const styles = useStyleConfig('TagToggle', { ...props, variant: themeVariant })
  const {
    h,
    fontSize: themeFontSize,
    paddingX = 0,
    paddingY = 0,
    gapX = 0,
    borderRadius,
    bgHover,
    bgPressed,
    ...containerColors
  } = styles.container ?? {}
  const iconStyles = styles.icon ?? {}

  const resolvedFontSize = fontSizes[themeFontSize as keyof typeof fontSizes] ?? themeFontSize
  const fontSize = `var(--vui-tag-font-size, ${resolvedFontSize}px)`

  const pxX = `${paddingX}px`
  const pxY = `${paddingY}px`
  const pxGap = `${gapX}px`

  return (
    <TagToggleBase
      aria-pressed={isSelected}
      borderRadius={borderRadius}
      className={cs('vui-tagToggle', className)}
      focusRing={2}
      fontSize={fontSize}
      h={h}
      onClick={() => onChange?.(!isSelected)}
      px={pxX}
      py={pxY}
      ref={ref}
      type="button"
      activeBg={bgPressed}
      hoverBg={bgHover}
      {...containerColors}
      {...rest}
    >
      {isString(icon) ? (
        <>
          <Icon className="vui-tagToggleIcon" mr={label ? pxGap : undefined} name={icon} {...iconStyles} />
          {label && (
            <T className="vui-tagToggleText" fontSize={fontSize}>
              {label}
            </T>
          )}
        </>
      ) : (
        label && (
          <T className="vui-tagToggleText" fontSize={fontSize}>
            {label}
          </T>
        )
      )}
    </TagToggleBase>
  )
})

TagToggle.displayName = 'TagToggle'

export default TagToggle
